import { ProgramBenchmarkName, ProgramTask, TaskDataTraitStatus } from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { Button, Stack } from '@mui/material'
import { MouseEvent, ReactNode, useState } from 'react'
import { TemplateTraitEditorPopover } from './TemplateTraitEditorPopover'

interface TemplateTraitCellContainerProps {
  value: any
  children: ReactNode
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  year: number
  traitId: string
  canEdit: boolean
  onValueSave: (traitId: string, year: number, filledValues: { value?: unknown; input?: unknown; result?: unknown }) => void
  onValueDelete: (traitId: string, year: number) => void
}

export const TemplateTraitCellContainer = ({
  value,
  children,
  task,
  year,
  traitId,
  canEdit,
  onValueSave,
  onValueDelete,
}: TemplateTraitCellContainerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSave = (filledValues: { value?: unknown; input?: unknown; result?: unknown }) => {
    onValueSave(traitId, year, filledValues)
    handleClose()
  }

  const handleDelete = () => {
    onValueDelete(traitId, year)
    handleClose()
  }

  if (!value) {
    if (!canEdit) return null
    return (
      <>
        <Button
          sx={{
            height: 64,
            p: 1,
            m: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
          }}
          onClick={handleOpen}
        >
          <AddIcon color="action" />
        </Button>
        <TemplateTraitEditorPopover
          anchorEl={anchorEl}
          onClose={handleClose}
          traitId={traitId}
          year={year}
          currentValue={value}
          task={task}
          onSave={handleSave}
        />
      </>
    )
  }

  return (
    <>
      <Button
        disabled={!canEdit}
        sx={{
          height: 64,
          borderRadius: '4px',
          m: 1,
          p: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
          textAlign: 'left',
          color: 'text.primary',
          '&:hover .edit-icon': { opacity: 1 },
        }}
        onClick={handleOpen}
      >
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          {children}
          {canEdit && (
            <EditIcon
              className="edit-icon"
              color="action"
              fontSize="small"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
            />
          )}
        </Stack>
      </Button>
      <TemplateTraitEditorPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        traitId={traitId}
        year={year}
        currentValue={value}
        task={task}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  )
}
